import type { Paginated } from './api';

export const REPORT_TIME_ZONE = 'Asia/Beirut' as const;

export type ReportCategory = 'space_hire' | 'desk_package' | 'event';
export type ReportSourceType = 'booking' | 'event_registration';
export type ReportPaymentMethod = 'cash' | 'whish' | 'omt' | 'other';

export interface AdminDashboardResponse {
  date: string;
  timeZone: typeof REPORT_TIME_ZONE;
  generatedAt: string;
  bookings: {
    total: number;
    desk: number;
    room: number;
  };
  payments: {
    collectedAmountMinor: number;
    paymentCount: number;
    outstandingAmountMinor: number;
    outstandingBookingCount: number;
    currency: 'USD';
  };
  expenses: {
    totalAmountMinor: number;
    expenseCount: number;
    netAmountMinor: number;
    currency: 'USD';
  };
  desks: {
    activeCount: number;
    occupiedCount: number;
    occupancyBasisPoints: number;
  };
  events: {
    upcomingEventCount: number;
    confirmedSeatCount: number;
    paymentPendingSeatCount: number;
  };
}

export type DashboardDrilldownMetric =
  'bookings' | 'collected' | 'outstanding' | 'occupied_desks' | 'upcoming_events';

export type AdminDashboardDrilldownRow =
  | {
      kind: 'booking';
      id: string;
      reference: string;
      customerName: string;
      resourceName: string;
      allocationType: 'desk' | 'room';
      status: 'payment_pending' | 'confirmed';
      totalAmountMinor: number;
      paidAmountMinor: number;
      balanceAmountMinor: number;
    }
  | {
      kind: 'payment';
      id: string;
      reference: string;
      sourceReference: string;
      customerName: string;
      itemName: string;
      amountMinor: number;
      recordedAt: string;
    }
  | {
      kind: 'desk';
      id: string;
      deskKey: string;
      resourceName: string;
      bookingId: string;
      bookingReference: string;
      customerName: string;
    }
  | {
      kind: 'event';
      id: string;
      title: string;
      eventDate: string;
      capacity: number;
      confirmedSeatCount: number;
      paymentPendingSeatCount: number;
    };

export type AdminDashboardDrilldownResponse = Paginated<AdminDashboardDrilldownRow> & {
  metric: DashboardDrilldownMetric;
  date: string;
  timeZone: typeof REPORT_TIME_ZONE;
  totals: {
    count: number;
    amountMinor: number | null;
    seatCount: number | null;
  };
};

export interface AdminReportFilters {
  from: string;
  to: string;
  facilityId: string | null;
  resourceId: string | null;
  customerId: string | null;
  eventId: string | null;
  category: ReportCategory | null;
  timeZone: typeof REPORT_TIME_ZONE;
}

export interface AdminAnalyticsSummary {
  collectedAmountMinor: number;
  expenseAmountMinor: number;
  netAmountMinor: number;
  expenseCount: number;
  paymentCount: number;
  saleCount: number;
  bookingCount: number;
  eventRegistrationCount: number;
  averageSaleAmountMinor: number;
  currency: 'USD';
}

export interface AdminExpenseAnalyticsRollup {
  key: string;
  label: string;
  expenseCount: number;
  expenseAmountMinor: number;
}

export interface AdminExpenseAnalyticsMonth {
  month: string;
  expenseCount: number;
  expenseAmountMinor: number;
}

export interface AdminAnalyticsRollup {
  key: string;
  label: string;
  saleCount: number;
  paymentCount: number;
  collectedAmountMinor: number;
}

export interface AdminAnalyticsMonth {
  month: string;
  saleCount: number;
  paymentCount: number;
  collectedAmountMinor: number;
}

export interface AdminAnalyticsResponse {
  filters: AdminReportFilters;
  summary: AdminAnalyticsSummary;
  byCategory: AdminAnalyticsRollup[];
  bySpace: AdminAnalyticsRollup[];
  byPackage: AdminAnalyticsRollup[];
  byEvent: AdminAnalyticsRollup[];
  byMonth: AdminAnalyticsMonth[];
  byMethod?: AdminAnalyticsRollup[];
  byCustomer?: AdminAnalyticsRollup[];
  byDay?: AdminAnalyticsPeriod[];
  byWeek?: AdminAnalyticsPeriod[];
  byWeekday?: AdminAnalyticsRollup[];
  expensesByMethod?: AdminExpenseAnalyticsRollup[];
  expensesByMonth?: AdminExpenseAnalyticsMonth[];
  receivables?: AdminAnalyticsReceivables;
  cancellations?: AdminAnalyticsCancellations;
  customers?: AdminAnalyticsCustomers;
}

export interface AdminAnalyticsPeriod {
  /** Beirut calendar day, or the Monday starting the ISO week (YYYY-MM-DD). */
  period: string;
  saleCount: number;
  paymentCount: number;
  collectedAmountMinor: number;
}

export interface AdminAnalyticsReceivables {
  billedAmountMinor: number;
  outstandingAmountMinor: number;
  /** These counts include event registrations as well as bookings. */
  outstandingBookingCount: number;
  paidInFullBookingCount: number;
  depositOnlyBookingCount: number;
  unpaidBookingCount: number;
  collectionRateBasisPoints: number;
  currency: 'USD';
}

export interface AdminAnalyticsCancellations {
  cancelledBookingCount: number;
  cancelledRegistrationCount: number;
  /** Lifetime payments retained on the cancelled bookings. */
  retainedDepositAmountMinor: number;
  currency: 'USD';
}

export interface AdminAnalyticsCustomers {
  newCustomerCount: number;
  returningCustomerCount: number;
}

export interface AdminReportTransaction {
  id: string;
  reference: string;
  sourceType: ReportSourceType;
  sourceId: string;
  sourceReference: string;
  category: ReportCategory;
  item: {
    id: string;
    key: string;
    name: string;
  };
  facility: {
    id: string;
    key: string;
    name: string;
  } | null;
  resource: {
    id: string;
    slug: string;
    name: string;
  } | null;
  customer: {
    id: string | null;
    reference: string | null;
    name: string;
    email: string;
  };
  amountMinor: number;
  currency: 'USD';
  method: ReportPaymentMethod;
  methodDetail: string;
  methodLabel: string;
  recordedDate: string;
  recordedAt: string;
}

export type AdminReportTransactionListResponse = Paginated<AdminReportTransaction> & {
  filters: AdminReportFilters;
  summary: AdminAnalyticsSummary;
};

export type AdminReportExportFormat = 'csv' | 'xlsx';
